import React, { useMemo, useState } from "react";
import {
    HiOutlineCalendar,
    HiOutlineTrendingUp,
    HiOutlineUsers,
    HiOutlineCollection,
    HiOutlineGift,
    HiOutlineShoppingBag,
    HiOutlineChartBar,
    HiOutlineClock,
    HiOutlineChevronDown,
    HiOutlineRefresh,
    HiOutlineUserGroup,
    HiOutlineCheck,
    HiOutlineExclamation,
    HiOutlineChartSquareBar,
} from "react-icons/hi";
import { useSelector } from "react-redux";
import LineChart from "../components/charts/LineChart";
import BarChart from "../components/charts/BarChart";
import Button from "../components/shared/Button";
import Card from "../components/shared/Card";
import Badge from "../components/shared/Badge";
import RevenueByMemberPanel from "../components/analytics/RevenueByMemberPanel";
import RevenueByGroupPanel from "../components/analytics/RevenueByGroupPanel";
import { buildMemberGroupMap, formatMemberWithGroups } from "../utils/memberGroups";
import "./Analytics.css";

// Stat cards alternate Card variants per DESIGN.md (heights vary deliberately).
const STAT_VARIANTS = ["default", "surface-soft", "cream-callout", "demo-grid"];

const Analytics = () => {
    const [dateRange, setDateRange] = useState("Last 30 Days");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showRefreshAnimation, setShowRefreshAnimation] = useState(false);
    const campaigns = useSelector((state) => state.organisation?.currentOrganisation?.campaigns);
    const members = useSelector((state) => state.organisation?.currentOrganisation?.members);
    const organisationId = useSelector((state) => state.organisation?.currentOrganisation?._id);
    const groups = useSelector((state) => state.organisation?.currentOrganisation?.groups);
    const memberGroupMap = useMemo(() => buildMemberGroupMap(groups), [groups]);
    const combinedPointsByMonth = calculateCumulativePoints();

    // Calculate key metrics
    const totalMembers = members ? members.length : 0;
    const activeCampaigns = campaigns ? campaigns.filter(campaign => campaign.status === "running").length : 0;
    const pointsRedeemed = members ? members.reduce((total, member) => {
        const memberPoints = member.redeemedPoints.reduce((sum, obj) => sum + obj.points, 0);
        return total + memberPoints;
    }, 0) : 0;

    const totalTransactions = members
        ? members.reduce((sum, member) => sum + (member.transactions ? member.transactions.length : 0), 0)
        : 0;

    const metrics = [
        {
            label: "Total Members",
            value: totalMembers,
            icon: <HiOutlineUsers className="stats-icon w-6 h-6" />,
        },
        {
            label: "Active Campaigns",
            value: activeCampaigns,
            icon: <HiOutlineCollection className="stats-icon w-6 h-6" />,
        },
        {
            label: "Points Redeemed",
            value: pointsRedeemed,
            icon: <HiOutlineGift className="stats-icon w-6 h-6" />,
        },
        {
            label: "Transactions",
            value: totalTransactions,
            icon: <HiOutlineShoppingBag className="stats-icon w-6 h-6" />,
        },
    ];

    const handleRefreshData = () => {
        setShowRefreshAnimation(true);
        setTimeout(() => setShowRefreshAnimation(false), 1000);
    };

    const toggleDatePicker = () => setShowDatePicker(!showDatePicker);
    const handleDateRangeChange = (range) => {
        setDateRange(range);
        setShowDatePicker(false);
    };

    function calculateCumulativePoints() {
        const currentYear = new Date().getFullYear();
        const monthNames = [
            "January", "February", "March", "April",
            "May", "June", "July", "August",
            "September", "October", "November", "December"
        ];

        const cumulativePoints = Array(12).fill(0);
        const allSubmissions = campaigns ? campaigns.flatMap(campaign => campaign.submissions) : [];
        const filteredSubmissions = allSubmissions.filter(submission => {
            const submissionYear = new Date(submission.timestamp).getFullYear();
            return submissionYear === currentYear;
        });

        filteredSubmissions.forEach(submission => {
            const date = new Date(submission.timestamp);
            const monthIndex = date.getMonth();
            cumulativePoints[monthIndex] += Number(submission.value);
        });

        for (let i = 1; i < cumulativePoints.length; i++) {
            cumulativePoints[i] += cumulativePoints[i - 1];
        }

        return monthNames.map((month, index) => ({
            month,
            cumulativePoints: cumulativePoints[index],
        }));
    }

    function getPointsForMonth(month) {
        if (combinedPointsByMonth != null) {
            return combinedPointsByMonth.find(m => m.month === month)?.cumulativePoints || 0;
        }
        return 0;
    }

    function calculateTransactionsForBarChart() {
        const currentYear = new Date().getFullYear();
        const result = { labels: [], values: [] };

        if (campaigns && campaigns.length > 0) {
            campaigns.forEach(campaign => {
                const filteredSubmissions = campaign.submissions.filter(submission => {
                    const submissionYear = new Date(submission.timestamp).getFullYear();
                    return submissionYear === currentYear;
                });
                result.labels.push(campaign.name);
                result.values.push(filteredSubmissions.length);
            });
        }
        return result;
    }

    function calculateTotalEarnedPoints() {
        if (!campaigns) return 0;
        const allSubmissions = campaigns.flatMap(campaign => campaign.submissions || []);
        return allSubmissions.reduce((sum, submission) => sum + (Number(submission.value) || 0), 0);
    }

    function calculateActiveMembers() {
        if (!members) return 0;
        return members.filter(member => member.transactions && member.transactions.length > 0).length;
    }

    function getTopCampaignsByPoints(limit = 5) {
        if (!campaigns) return [];
        return campaigns
            .slice()
            .map((campaign) => {
                const campaignPoints = campaign.submissions?.reduce(
                    (sum, submission) => sum + (Number(submission.value) || 0),
                    0
                ) || 0;
                return { ...campaign, campaignPoints };
            })
            .sort((a, b) => b.campaignPoints - a.campaignPoints)
            .slice(0, limit);
    }

    return (
        <div className="analytics-container min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-surface-soft">
                            <HiOutlineChartBar className="h-6 w-6 text-ink" />
                        </div>
                        <h1 className="text-display-md text-ink" data-help-title="Analytics" data-help="Gives you a full picture of how your loyalty program is performing: how many members you have, how active they are, which campaigns are working, and how much revenue is flowing through the program.">Analytics</h1>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button
                            variant="icon-circular"
                            onClick={handleRefreshData}
                            aria-label="Refresh data"
                            data-help-title="Refresh data"
                            data-help="Gives the dashboard a quick visual refresh. The figures here are computed from data already loaded in the app, so they stay current automatically."
                        >
                            <HiOutlineRefresh className={`h-5 w-5 ${showRefreshAnimation ? 'animate-spin' : ''}`} />
                        </Button>

                        <div className="relative">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={toggleDatePicker}
                                leadingIcon={<HiOutlineCalendar className="h-4 w-4" />}
                                trailingIcon={<HiOutlineChevronDown className="h-4 w-4" />}
                                data-help-title="Date range"
                                data-help="Opens the date range selector: Last 7 Days, Last 30 Days, Last 90 Days, This Year, or All Time. The chosen range scopes the Revenue by Member and Revenue by Group panels."
                            >
                                {dateRange}
                            </Button>

                            <div
                                className={`absolute right-0 mt-2 w-48 rounded-md shadow-sm z-10 border border-hairline bg-canvas dropdown-menu ${showDatePicker ? 'active' : ''}`}
                            >
                                <div className="p-2">
                                    {["Last 7 Days", "Last 30 Days", "Last 90 Days", "This Year", "All Time"].map((range) => (
                                        <button
                                            key={range}
                                            onClick={() => handleDateRangeChange(range)}
                                            data-help-title="Date range option"
                                            data-help="Applies this time window to the revenue panels and closes the selector."
                                            className={`block w-full text-left px-4 py-2 text-body-md rounded-sm ${
                                                dateRange === range ? 'bg-surface-soft text-ink' : 'text-body'
                                            }`}
                                        >
                                            {range}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Revenue by Member — prominent placement at top */}
                <div className="mb-8" data-help-title="Revenue by Member" data-help="Surfaces your highest-revenue members for the selected date range. Click into it to open the full Revenue by Member report, where you can search by name or identification.">
                    <RevenueByMemberPanel organisationId={organisationId} dateRange={dateRange} />
                </div>

                <div className="mb-8" data-help-title="Revenue by Group" data-help="Surfaces your highest-revenue groups for the selected date range. Click into it to open the full Revenue by Group report, where each row jumps to that group's detail page.">
                    <RevenueByGroupPanel organisationId={organisationId} dateRange={dateRange} />
                </div>

                {/* Stat Cards — varied surface variants per DESIGN.md */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                    {metrics.map((metric, index) => (
                        <Card
                            key={index}
                            variant={STAT_VARIANTS[index % STAT_VARIANTS.length]}
                            className="flex items-start justify-between"
                            data-help-title="Key metric"
                            data-help="Shows a live key figure: Total Members counts everyone in your program, Active Campaigns counts campaigns currently running, Points Redeemed sums points members have spent, and Transactions counts all recorded activities."
                        >
                            <div>
                                <p className="text-caption text-muted uppercase tracking-wide mb-2">
                                    {metric.label}
                                </p>
                                <h3 className="text-display-md text-ink">
                                    {metric.value.toLocaleString()}
                                </h3>
                            </div>
                            <div className="text-ink opacity-70">
                                {metric.icon}
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <Card variant="default" data-help-title="Points Trend" data-help="Shows cumulative points awarded across the current year, broken down by month. Below the chart you also see total points and the monthly average.">
                        <h2 className="text-title-md text-ink mb-5 flex items-center gap-2">
                            <HiOutlineTrendingUp className="h-5 w-5 text-ink" />
                            <span>Points Trend</span>
                        </h2>
                        <div className="h-64">
                            <LineChart data={{
                                labels: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
                                values: [
                                    getPointsForMonth("January"), getPointsForMonth("February"), getPointsForMonth("March"),
                                    getPointsForMonth("April"), getPointsForMonth("May"), getPointsForMonth("June"),
                                    getPointsForMonth("July"), getPointsForMonth("August"), getPointsForMonth("September"),
                                    getPointsForMonth("October"), getPointsForMonth("November"), getPointsForMonth("December"),
                                ],
                            }} />
                        </div>
                        <div className="mt-4 p-3 rounded-md bg-surface-soft">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-body-md text-muted">Total Points</p>
                                    <p className="text-title-sm text-ink">
                                        {calculateTotalEarnedPoints().toLocaleString()}
                                    </p>
                                </div>
                                <div className="h-8 w-px bg-hairline" />
                                <div>
                                    <p className="text-body-md text-muted">Monthly Average</p>
                                    <p className="text-title-sm text-ink">
                                        {Math.round(calculateTotalEarnedPoints() / 12).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card variant="default" data-help-title="Campaign Performance" data-help="Compares how many submissions each campaign has received this year, so you can spot which campaigns are pulling their weight and which are quiet. The footer shows active campaigns and the share of members with at least one transaction.">
                        <h2 className="text-title-md text-ink mb-5 flex items-center gap-2">
                            <HiOutlineChartSquareBar className="h-5 w-5 text-ink" />
                            <span>Campaign Performance</span>
                        </h2>
                        <div className="h-64">
                            <BarChart data={calculateTransactionsForBarChart()} />
                        </div>
                        <div className="mt-4 p-3 rounded-md bg-surface-soft">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-body-md text-muted">Active Campaigns</p>
                                    <p className="text-title-sm text-ink">
                                        {activeCampaigns.toLocaleString()}
                                    </p>
                                </div>
                                <div className="h-8 w-px bg-hairline" />
                                <div>
                                    <p className="text-body-md text-muted">Conversion Rate</p>
                                    <p className="text-title-sm text-ink">
                                        {totalMembers ? Math.round((calculateActiveMembers() / totalMembers) * 100) : 0}%
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Top Campaigns / Top Members */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card variant="default" className="!p-0 overflow-hidden" data-help-title="Top Campaigns" data-help="Lists your five highest-earning campaigns, ranked by total points awarded, with each campaign's status badge.">
                        <div className="p-5 border-b border-hairline flex items-center justify-between">
                            <h2 className="text-title-md text-ink flex items-center gap-2">
                                <HiOutlineCollection className="h-5 w-5 text-ink" />
                                <span>Top Campaigns</span>
                            </h2>
                            <Badge variant="default">
                                Top {Math.min(5, campaigns?.length || 0)}
                            </Badge>
                        </div>

                        <div className="px-5 py-3 bg-surface-soft border-b border-hairline">
                            <div className="flex text-caption text-muted uppercase tracking-wide">
                                <div className="w-1/2 px-2">Campaign</div>
                                <div className="w-1/4 px-2 text-right">Points</div>
                                <div className="w-1/4 px-2 text-right">Status</div>
                            </div>
                        </div>

                        <ul>
                            {getTopCampaignsByPoints(5).map((campaign, index) => (
                                <li
                                    key={index}
                                    className="campaign-item border-b border-hairline px-5 py-4"
                                >
                                    <div className="flex items-center">
                                        <div className="w-1/2 px-2">
                                            <div className="text-body-md font-medium text-ink">
                                                {campaign.name}
                                            </div>
                                            <div className="text-body-md mt-1 flex items-center text-muted">
                                                <HiOutlineClock className="mr-1 h-3 w-3" />
                                                <span>
                                                    {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="w-1/4 px-2 text-right text-title-sm text-ink">
                                            {campaign.campaignPoints.toLocaleString()}
                                        </div>
                                        <div className="w-1/4 px-2 text-right">
                                            {campaign.status === "running" ? (
                                                <Badge variant="success">
                                                    <HiOutlineCheck className="mr-1 h-3 w-3" /> Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="warning">
                                                    <HiOutlineExclamation className="mr-1 h-3 w-3" /> Inactive
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </Card>

                    <Card variant="default" className="!p-0 overflow-hidden" data-help-title="Top Members" data-help="Lists the five members with the highest points balance, including their submission count, current points, and points already redeemed.">
                        <div className="p-5 border-b border-hairline flex items-center justify-between">
                            <h2 className="text-title-md text-ink flex items-center gap-2">
                                <HiOutlineUserGroup className="h-5 w-5 text-ink" />
                                <span>Top Members</span>
                            </h2>
                            <Badge variant="default">By Points</Badge>
                        </div>

                        <div className="px-5 py-3 bg-surface-soft border-b border-hairline">
                            <div className="flex text-caption text-muted uppercase tracking-wide">
                                <div className="w-2/5 px-2">Member</div>
                                <div className="w-1/5 px-2 text-center">Trxns</div>
                                <div className="w-1/5 px-2 text-center">Points</div>
                                <div className="w-1/5 px-2 text-center">Redeemed</div>
                            </div>
                        </div>

                        <ul>
                            {members
                                ?.slice()
                                .sort((a, b) => (b.points || 0) - (a.points || 0))
                                .slice(0, 5)
                                .map((member, index) => {
                                    const totalRedeemedPoints = member.redeemedPoints?.reduce(
                                        (sum, entry) => sum + (entry.points || 0),
                                        0
                                    ) || 0;

                                    return (
                                        <li
                                            key={index}
                                            className="member-item border-b border-hairline px-5 py-4"
                                        >
                                            <div className="flex items-center">
                                                <div className="w-2/5 px-2">
                                                    <div className="text-body-md font-medium text-ink">
                                                        {formatMemberWithGroups(member, memberGroupMap, "No Name")}
                                                    </div>
                                                    <div className="text-body-md mt-1 truncate max-w-[200px] text-muted" title={member.identification}>
                                                        ID: {member.identification}
                                                    </div>
                                                </div>
                                                <div className="w-1/5 px-2 text-center text-body-md text-body">
                                                    {(member.transactions?.length || 0).toLocaleString()}
                                                </div>
                                                <div className="w-1/5 px-2 text-center text-body-md font-medium text-ink">
                                                    {(member.points || 0).toLocaleString()}
                                                </div>
                                                <div className="w-1/5 px-2 text-center text-body-md text-body">
                                                    {totalRedeemedPoints.toLocaleString()}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                        </ul>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
